import React, { useState, useRef, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { DateTime } from "luxon"
import { useReactToPrint } from 'react-to-print'
import { SampleTable, Loading } from "components"
import { Button } from "react-bootstrap"
import { samples, message, analyses } from "state_management"
import useT from "utils/hooks/useT"
import { ConfirmDialog, Popup } from "components"
import { Container } from "components/Form"
import { FiInfo, FiPrinter } from "react-icons/fi"
import { FaLockOpen, FaLock } from "react-icons/fa"
import PlannableSamples from "./PlannableSamples"
import SampleTableForPrinting from "./SampleTableForPrinting"
import isOnMobile from "utils/scripts/isOnMobile"

const PrintComponent = React.forwardRef(({ data }, ref) => {
  // Forward the ref to the printed div
  return (
    <div className="print-container" ref={ref}>
      <SampleTableForPrinting popupSamples={data} />
    </div>
  )
})

// SPEC-045 K1-grace: is the analysis inside the one-day manual "add straggler sample" window?
// Its EARLIEST task start must fall in [yesterday 00:00, today 00:00) in the team timezone.
// Mirrors backend assign_samples exactly (get_lock_boundary(tz) → today 00:00; yesterday = that
// minus one day; allowed iff yesterday <= earliest < today). Returns false when the analysis
// started 2+ days ago, is a manually-locked future analysis (earliest >= today 00:00), or has no
// dated tasks. The grace is manual-only — the automatic Fill-In never targets a locked analysis —
// so only the "Add Samples" button consults this.
// SPEC-046 (F1): reads the authoritative `analysis.earliestTaskStart` (serialized by the backend
// as the min task start over ALL tasks, SPEC-047) instead of scanning the date-windowed
// `state.analysisTasks` store, which only holds the tasks of the currently fetched schedule window
// and therefore mis-judged analyses whose earliest task lay outside it.
function isWithinAddSampleGrace(analysis, teamTimeZone) {
  if (!analysis?.earliestTaskStart) return false
  const today = DateTime.now().setZone(teamTimeZone).startOf("day")
  const yesterday = today.minus({ days: 1 })
  const earliest = DateTime.fromISO(analysis.earliestTaskStart)
  return earliest >= yesterday && earliest < today
}

export default function SampleTableForPlannedAnalysis({ analysisId, cancelHandler, readOnly = false }) {
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")
  const sampleData = useSelector((state) => state.samples[analysisId] || null)
  const [unplanConfirm, setUnplanConfirm] = useState(false)
  // SPEC-032: non-LIMS teams keep the legacy "Fail Selected" → rerun flow. LIMS teams fail via the
  // QC Results & Approvals / OOS flow instead, so the button below is gated on !showLims.
  const [failConfirm, setFailConfirm] = useState(false)
  const [showUnplannedSamples, setShowUnplannedSamples] = useState(false)
  const [checkedSamples, setCheckedSamples] = useState([])
  const analysis = useSelector((state) => state.analyses.plannedAnalyses[analysisId])
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const hasExclusiveSample = sampleData?.items ? sampleData.items.some(sample => sample.exclusive === true) : false

  // SPEC-045 (K11): a hard-locked analysis (samplesLocked === true) refuses add/unplan of samples
  // server-side, so those actions are ghosted below. Fail (K10) and the lock toggle stay available.
  const isLocked = !!analysis?.samplesLocked
  // SPEC-045 (K14): the unlock (FaLock) action is refused while the analysis has a task before
  // today 00:00 (team tz) — i.e. it is auto-locked, not merely manually locked. Mirror that fixed
  // today-boundary (backend get_lock_boundary) so the unlock toggle is ghosted rather than failing.
  // SPEC-046 (F1): read the authoritative `analysis.earliestTaskStart` instead of scanning the
  // date-windowed task store. (F2): evaluated as plain per-render consts — NOT useMemo'd on stable
  // deps — so a popup left open across midnight re-evaluates against the current clock; the
  // one-field comparison is cheap.
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const lockBoundary = DateTime.now().setZone(teamTimeZone).startOf("day")
  const hasTaskInPast =
    analysis?.earliestTaskStart != null && DateTime.fromISO(analysis.earliestTaskStart) < lockBoundary

  // SPEC-045 K1-grace (customer feedback / backend PR #294): a hard-locked analysis still accepts a
  // MANUAL straggler sample for ONE day after it started (see isWithinAddSampleGrace). "Add Samples"
  // stays available while NOT hard-locked, OR within that one-day grace; unplan/delete have no grace.
  // SPEC-047 (R16): an EXECUTED analysis (DONE/FAILED samples) also blocks add — subject to the
  // SAME one-day grace, so a straggler can still be added the day after it started.
  // SPEC-046 follow-up (adversarial review): hasTaskInPast is OR'ed in as the date backstop, so the
  // ghost matches the backend's flag-AND-date guard (R2) even under flag drift or a popup left open
  // across midnight — the backend refuses either way; the button must not promise otherwise.
  const addSamplesBlocked =
    (isLocked || hasTaskInPast || !!analysis?.hasCompletedOrFailedSamples) &&
    !isWithinAddSampleGrace(analysis, teamTimeZone)
  // Reason-aware tooltip for the ghosted "Add Samples" button. Executed is the strongest reason
  // (it can't be lifted by unlocking or moving the analysis forward), so it wins; otherwise an
  // auto-locked past analysis points at moving it forward (K14) and a manually-locked future one
  // at unlocking it.
  let addBlockedTooltipKey = "blockedActionLocked"
  if (analysis?.hasCompletedOrFailedSamples) addBlockedTooltipKey = "blockedActionAddCompleted"
  else if (hasTaskInPast) addBlockedTooltipKey = "blockedActionStarted"

  // SPEC-047 (R19): the backend refuses failing a sample until its analysis has STARTED — its
  // earliest task lies today or earlier (< tomorrow 00:00 team tz; note the +1 day vs the lock
  // boundary: an analysis whose first task runs TODAY is already failable, incl. mid-run on a
  // multi-day analysis). Failing asserts the run was attempted, so — like the R18 mark-done
  // ghost — it cannot be claimed ahead of time. Reads the authoritative earliestTaskStart;
  // no dated tasks (or missing field on an older BE) → not started → ghost, fail-closed.
  const failBlocked = !(
    analysis?.earliestTaskStart != null &&
    DateTime.fromISO(analysis.earliestTaskStart) < lockBoundary.plus({ days: 1 })
  )

  // Flag the selected samples (out of trend). Flagging is reversible, so no confirm dialog.
  // SPEC-020 (C5): flag/fail here no longer raise LIMS OOS/OOT register entries — those records
  // had no Phase-1 investigation, no UI and no close path (orphans). A formal OOS is opened ONLY
  // via the investigation modal (FAIL card / QA worklist).
  const handleFlagSelected = () => {
    if (checkedSamples.length === 0) {
      dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
      return
    }
    const selected = (sampleData.items || []).filter((s) => checkedSamples.includes(s.id))
    selected.forEach((s) => dispatch(samples.flagSample(s.id, s.sampleName || "")))
    setCheckedSamples([])
  }

  const printRef = useRef(null)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
  })

  // If the analysis no longer exists (e.g., all samples were unplanned), close the popup
  useEffect(() => {
    if (!analysis && sampleData && !sampleData.isLoading) {
      cancelHandler()
    }
  }, [analysis, sampleData, cancelHandler])

  // Fetch the planned analysis's samples on mount / when the analysis changes. This MUST run in
  // an effect, not during render — dispatching during render previously updated sibling state
  // mid-render and triggered React's "cannot update a component while rendering" warning.
  useEffect(() => {
    if (!sampleData) {
      dispatch(samples.fetchForPlanned(analysisId))
    }
  }, [analysisId, sampleData, dispatch])

  // Blank into the spinner only on the FIRST load (no items yet). During a refetch the previous
  // items are kept in state (stale-while-revalidate, see the samples reducer) so the popup stays
  // visible — otherwise the SPEC-047 optimistic fail state was replaced by a full-popup spinner
  // the moment the success refetches began, which read as "failing lags".
  if (!sampleData || (sampleData.isLoading && !sampleData.items)) {
    return <Loading />
  }
  
  // If analysis doesn't exist, show loading while closing
  if (!analysis) {
    return <Loading />
  }

  return (
    <>
      {showUnplannedSamples && (
        <Popup className="sampletable-addsamples-popup" onCancel={() => setShowUnplannedSamples(false)}>
          <PlannableSamples analysisId={analysisId} handler={() => setShowUnplannedSamples(false)} />
        </Popup>
      )}

      {failConfirm && (
        <ConfirmDialog
          onCancel={() => {
            setFailConfirm(false)
          }}
          onConfirm={() => {
            dispatch(samples.failSamples(analysisId, checkedSamples))
            setCheckedSamples([])
            setFailConfirm(false)
          }}
        >
          <Container>
            <div className="FailSamples-info-text">
              <FiInfo title={tc("confirmFailSamplesInfo")} />
            </div>
            <h4>{tc("confirmFailSamplesQuestion")}</h4>
          </Container>
        </ConfirmDialog>
      )}
      {unplanConfirm && (
        <ConfirmDialog
          onCancel={() => {
            setUnplanConfirm(false)
          }}
          onConfirm={() => {
            // Check if we're unplanning all samples
            const isUnplanningAllSamples = checkedSamples.length === sampleData.items.length

            dispatch(samples.unplanSamples(analysisId,checkedSamples,sampleData.items.filter(sample => checkedSamples.indexOf(sample.id) !== -1)))
              .then((res) => {
                // SPEC-046 (F3): close the popup only when the unplan fully succeeded — the backend
                // answers 204 (res == null) then. A 200 body carries `refused` entries (SPEC-013
                // partial refusal, e.g. DONE samples on a locked analysis): keep the popup open so
                // the thunk's refetch shows the surviving samples; its warning toast (res.message)
                // explains what was refused. If the analysis DID empty out anyway, the
                // analysis-gone effect above closes the popup as before.
                if (isUnplanningAllSamples && res == null) {
                  cancelHandler()
                }
              })
            setCheckedSamples([])
            setUnplanConfirm(false)
          }}
        >
          <Container>
            <div className="FailSamples-info-text">
              <FiInfo title={tc("confirmUnplanSamplesInfo")} />
            </div>
            <h4>{tc("confirmUnplanSamplesQuestion")}</h4>
          </Container>
        </ConfirmDialog>
      )}
      {!readOnly && (
        <div className="add-samples-asset-view">
          {!isOnMobile && (
            <>
              <p style={{ marginRight: "0.5rem" }}>{tc("lockUnlockSamplesLabel")} </p>
              {analysis.samplesLocked ? (
                hasTaskInPast || analysis.hasCompletedOrFailedSamples ? (
                  // SPEC-045 K14 / SPEC-047 R16: unlock is refused server-side — either the
                  // analysis is auto-locked (a task in the past; move it forward first) or it is
                  // EXECUTED (has DONE/FAILED samples and can never be unlocked). Ghost the icon
                  // (no onClick, reason-aware tooltip).
                  <FaLock
                    title={tc(
                      analysis.hasCompletedOrFailedSamples ? "blockedActionUnlockCompleted" : "cannotUnlockPastAnalysis"
                    )}
                    className="analysis-samples-lock blocked-action-icon"
                  />
                ) : (
                  <FaLock
                    title={tc("unlockSamples")}
                    className="analysis-samples-lock"
                    onClick={() => {
                      dispatch(analyses.setSamplesLocked(analysisId, false));
                    }}
                  />
                )
              ) : (
                  <FaLockOpen
                    title={tc("lockSamples")}
                    className="analysis-samples-lock"
                    onClick={() => {
                      dispatch(analyses.setSamplesLocked(analysisId, true));
                    }}
                  />
                )}
            </>
          )}

          {addSamplesBlocked ? (
            // SPEC-045 K1: no adding samples to a hard-locked analysis — except within the one-day
            // manual grace (K1-grace), when the button stays active below. SPEC-047 (R16): an
            // executed analysis (DONE/FAILED samples) blocks add too (same grace window).
            // Reason-aware tooltip (SPEC-046 F4 + R16): executed → "no new samples"; auto-locked
            // past analysis → "move it forward" (unlock is dead-end, K14); else "unlock it".
            <span
              className="blocked-action-wrap"
              title={tc(addBlockedTooltipKey)}
            >
              <Button variant="secondary" size="lg" className="blocked-action-btn" disabled>
                {tc("addSamples")}
              </Button>
            </span>
          ) : (
            <Button
              variant="success"
              size="lg"
              onClick={ () => setShowUnplannedSamples(true) }
            >
              {tc("addSamples")}
            </Button>
          )}
        </div>
      )}
      <div className="sample-table-planned-main-container">
        <div className="sample-table-planned">
          <SampleTable popupSamples={sampleData.items} checkedSamples={checkedSamples} setCheckedSamples={readOnly ? null : setCheckedSamples} analysisId={analysisId} />
        </div>
      </div>
      {hasExclusiveSample && (
        <div className="explanation-text">
          {tc("exclusiveSampleIndicator")}
        </div>
      )}
      <div className="sampletable-buttons">
        <Button variant="secondary" onClick={handlePrint}> <FiPrinter /></Button>
        <PrintComponent data={sampleData.items} ref={printRef} />
        <div className="sampletable-cancel">
          <Button
            variant="light"
            onClick={() => {
              cancelHandler()
            }}
          >
            {tc("cancel")}
          </Button>
        </div>
        {!readOnly && (
          <div className="confirm-buttons">
            {isLocked || hasTaskInPast ? (
              // SPEC-045 K2: no unplanning samples from a hard-locked analysis.
              // SPEC-046 follow-up (adversarial review): hasTaskInPast OR'ed in as the date
              // backstop — the backend unplan guard refuses ANALYSIS_TASK_IN_PAST regardless of
              // the lock flag, so the button must ghost on either signal.
              // SPEC-046 (F4): reason-aware tooltip (see the Add Samples ghost above).
              <span
                className="blocked-action-wrap"
                title={tc(hasTaskInPast ? "blockedActionStarted" : "blockedActionLocked")}
              >
                <Button variant="secondary" className="blocked-action-btn" disabled>
                  {isOnMobile ? tc("unplan") : tc("unplanSelected")}
                </Button>
              </span>
            ) : (
              <Button
                variant="danger"
                onClick={() => {
                  // If no checkboxes are clicked
                  if(checkedSamples.length === 0) {
                    dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
                    cancelHandler()
                  } else {
                      setUnplanConfirm(true)
                    }
                }}
              >
              {isOnMobile ? tc("unplan") : tc("unplanSelected")}
              </Button>
            )}
            {!showLims && (failBlocked ? (
              // SPEC-047 (R19): ghost Fail until the analysis has started — the backend refuses
              // with 406 before then (a sample cannot fail before its run was attempted).
              <span className="blocked-action-wrap" title={tc("blockedActionFailNotStarted")}>
                <Button variant="secondary" className="blocked-action-btn" disabled>
                  {isOnMobile ? tc("fail") : tc("failSelected")}
                </Button>
              </span>
            ) : (
              <Button
                className="sampletable-fail-button"
                onClick={() => {
                  // SPEC-032: fail the selected samples → backend creates a rerun
                  // (DELETE /samples/fail-samples). Non-LIMS only; LIMS teams fail via QC Results.
                  if (checkedSamples.length === 0) {
                    dispatch(message.warning(t("adhoc.noCheckboxesSelected")))
                    cancelHandler()
                  } else {
                    setFailConfirm(true)
                  }
                }}
              >
                {isOnMobile ? tc("fail") : tc("failSelected")}
              </Button>
            ))}
            {showLims && (
              <Button
                variant="warning"
                className="sampletable-flag-button"
                onClick={handleFlagSelected}
              >
                {isOnMobile ? tc("flag") : tc("flagSelected")}
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  )
}
